import * as i0 from '@angular/core';
import { Injectable, PLATFORM_ID, Directive, Inject, NgModule, InjectionToken } from '@angular/core';
import { APP_BASE_HREF, isPlatformBrowser, CommonModule, DOCUMENT } from '@angular/common';
import { isNil } from 'ramda';
import * as i1 from '@angular/cdk/a11y';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { throwError, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { generate } from 'short-uuid';

const NO_CACHE_HEADER = 'X-No-Cache';
class CacheInterceptor {
    intercept(req, next) {
        if (req.headers.has(NO_CACHE_HEADER)) {
            const busted = req.clone({
                setParams: {
                    _d: `${new Date().getTime()}`,
                },
                headers: req.headers.delete(NO_CACHE_HEADER),
            });
            return next.handle(busted);
        }
        return next.handle(req);
    }
}
CacheInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CacheInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
CacheInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CacheInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CacheInterceptor, decorators: [{
            type: Injectable
        }] });

const isBlank = (obj) => obj === undefined || obj === null;
const isPresent = (obj) => obj !== undefined && obj !== null;
const isString = (obj) => typeof obj === 'string';
const mergeOptions = (oldOptions, newOptions) => {
    if (!newOptions) {
        return oldOptions;
    }
    return {
        path: isPresent(newOptions.path) ? newOptions.path : oldOptions.path,
        domain: isPresent(newOptions.domain) ? newOptions.domain : oldOptions.domain,
        expires: isPresent(newOptions.expires) ? newOptions.expires : oldOptions.expires,
        secure: isPresent(newOptions.secure) ? newOptions.secure : oldOptions.secure,
        httpOnly: isPresent(newOptions.httpOnly) ? newOptions.httpOnly : oldOptions.httpOnly,
        storeUnencoded: isPresent(newOptions.storeUnencoded) ? newOptions.storeUnencoded : oldOptions.storeUnencoded,
    };
};
const safeDecodeURIComponent = (str) => {
    try {
        return decodeURIComponent(str);
    }
    catch (e) {
        return str;
    }
};
const safeJsonParse = (str) => {
    try {
        return JSON.parse(str);
    }
    catch (e) {
        return str;
    }
};

class CookieService {
    constructor(injector) {
        this.injector = injector;
        this.options = {
            path: this.injector.get(APP_BASE_HREF, '/'),
            domain: undefined,
            expires: undefined,
            secure: false,
            httpOnly: false,
        };
    }
    get cookieString() {
        return document.cookie || '';
    }
    set cookieString(val) {
        document.cookie = val;
    }
    /**
     * @name CookieService#get
     *
     * @description
     * Returns the value of given cookie key.
     *
     * @param key Id to use for lookup.
     * @returns Raw cookie value.
     */
    get(key) {
        return this.cookieReader()[key];
    }
    /**
     * @name CookieService#getObject
     *
     * @description
     * Returns the deserialized value of given cookie key.
     *
     * @param key Id to use for lookup.
     * @returns Deserialized cookie value.
     */
    getObject(key) {
        const value = this.get(key);
        return value ? safeJsonParse(value) : value;
    }
    /**
     * @name CookieService#getAll
     *
     * @description
     * Returns a key value object with all the cookies.
     *
     * @returns All cookies
     */
    getAll() {
        return this.cookieReader();
    }
    /**
     * @name CookieService#put
     *
     * @description
     * Sets a value for given cookie key.
     *
     * @param key Id for the `value`.
     * @param value Raw value to be stored.
     * @param options (Optional) Options object.
     */
    put(key, value, options) {
        this.cookieWriter()(key, value, options);
    }
    /**
     * @name CookieService#putObject
     *
     * @description
     * Serializes and sets a value for given cookie key.
     *
     * @param key Id for the `value`.
     * @param value Value to be stored.
     * @param options (Optional) Options object.
     */
    putObject(key, value, options) {
        this.put(key, JSON.stringify(value), options);
    }
    /**
     * @name CookieService#remove
     *
     * @description
     * Remove given cookie.
     *
     * @param key Id of the key-value pair to delete.
     * @param options (Optional) Options object.
     */
    remove(key, options) {
        this.cookieWriter()(key, null, options);
    }
    /**
     * @name CookieService#removeAll
     *
     * @description
     * Remove all cookies.
     */
    removeAll(options) {
        const cookies = this.getAll();
        Object.keys(cookies).forEach((key) => {
            this.remove(key, options);
        });
    }
    cookieReader() {
        let lastCookies = {};
        let lastCookieString = '';
        let cookieArray;
        let cookie;
        let i;
        let index;
        let name;
        const currentCookieString = this.cookieString;
        if (currentCookieString !== lastCookieString) {
            lastCookieString = currentCookieString;
            cookieArray = lastCookieString.split('; ');
            lastCookies = {};
            for (i = 0; i < cookieArray.length; i++) {
                cookie = cookieArray[i];
                index = cookie.indexOf('=');
                if (index > 0) {
                    // ignore nameless cookies
                    name = safeDecodeURIComponent(cookie.substring(0, index));
                    // the first value that is seen for a cookie is the most
                    // specific one.  values for the same cookie name that
                    // follow are for less specific paths.
                    if (isBlank(lastCookies[name])) {
                        lastCookies[name] = safeDecodeURIComponent(cookie.substring(index + 1));
                    }
                }
            }
        }
        return lastCookies;
    }
    cookieWriter() {
        const that = this;
        return (name, value, options) => {
            that.cookieString = that.buildCookieString(name, value, options);
        };
    }
    buildCookieString(name, value, options) {
        const opts = mergeOptions(this.options, options);
        let expires = opts.expires;
        if (isNil(value)) {
            expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
            value = '';
        }
        if (isString(expires)) {
            expires = new Date(expires);
        }
        const cookieValue = opts.storeUnencoded ? value : encodeURIComponent(value);
        let str = encodeURIComponent(name) + '=' + cookieValue;
        str += opts.path ? ';path=' + opts.path : '';
        str += opts.domain ? ';domain=' + opts.domain : '';
        str += expires ? ';expires=' + expires.toUTCString() : '';
        str += opts.secure ? ';secure' : '';
        str += opts.httpOnly ? '; HttpOnly' : '';
        // per http://www.ietf.org/rfc/rfc2109.txt browser must allow at minimum:
        // - 300 cookies
        // - 20 cookies per unique domain
        // - 4096 bytes per cookie
        const cookieLength = str.length + 1;
        if (cookieLength > 4096) {
            console.warn(`Cookie \'${name}\' possibly not set or overflowed because it was too large (${cookieLength} > 4096 bytes)!`);
        }
        return str;
    }
}
CookieService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CookieService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
CookieService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CookieService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CookieService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

class FocusDirective {
    constructor(element, 
    // eslint-disable-next-line @typescript-eslint/ban-types
    platform, changeDetectorRef, ic) {
        this.element = element;
        this.platform = platform;
        this.changeDetectorRef = changeDetectorRef;
        this.ic = ic;
    }
    ngAfterViewInit() {
        if (isPlatformBrowser(this.platform)) {
            if (!this.ic.isFocusable(this.element.nativeElement) && !this.element.nativeElement.hasAttribute('tabindex')) {
                this.element.nativeElement.tabIndex = -1;
            }
            this.element.nativeElement.focus();
            this.changeDetectorRef.detectChanges();
        }
    }
}
FocusDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusDirective, deps: [{ token: i0.ElementRef }, { token: PLATFORM_ID }, { token: i0.ChangeDetectorRef }, { token: i1.InteractivityChecker }], target: i0.ɵɵFactoryTarget.Directive });
FocusDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.10", type: FocusDirective, selector: "[utAutoFocus]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[utAutoFocus]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ChangeDetectorRef }, { type: i1.InteractivityChecker }]; } });

class FocusModule {
}
FocusModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FocusModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: FocusModule, declarations: [FocusDirective], imports: [CommonModule], exports: [FocusDirective] });
FocusModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FocusModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [FocusDirective],
                    exports: [FocusDirective],
                }]
        }] });

/**
 * @deprecated If needed, create a utility in the app to handle errors
 */
class HttpErrorService {
    constructor() { }
    /**
     * @deprecated If needed, create a utility in the app to handle errors
     */
    handleError(error, defaultMessage) {
        const errMsg = (error instanceof HttpErrorResponse ? (error.error ? error.error.errorMessage : '') : error) ||
            defaultMessage ||
            'Oops, something went wrong!';
        return throwError(() => errMsg);
    }
}
HttpErrorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpErrorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
HttpErrorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpErrorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpErrorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

class LoadingEvents {
    constructor(doc) {
        this.doc = doc;
        this.isAppReady = false;
    }
    triggerAppReady() {
        if (this.isAppReady) {
            return;
        }
        this.doc.dispatchEvent(this.createEvent('appready', true, false));
        this.isAppReady = true;
    }
    createEvent(eventType, bubbles, cancelable) {
        const customEvent = new CustomEvent(eventType, {
            bubbles,
            cancelable,
        });
        return customEvent;
    }
}
LoadingEvents.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoadingEvents, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable });
LoadingEvents.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoadingEvents, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoadingEvents, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }]; } });

const getWindow = () => window;
class WindowService {
    get nativeWindow() {
        return getWindow();
    }
    load(url) {
        getWindow().location.href = url;
    }
    openExternal(url) {
        const win = this.nativeWindow.open(url, '_blank');
        try {
            win.opener = undefined;
        }
        catch (err) { }
    }
}
WindowService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WindowService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
WindowService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WindowService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WindowService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

const KEY = '__test__';
class CookieStorage {
    constructor(cookieService) {
        this.cookieService = cookieService;
        this.prefix = '_PSU_LS_';
    }
    clear() {
        const cookies = this.cookieService.getAll();
        for (const key in cookies) {
            if (key.startsWith(this.prefix)) {
                this.removeItem(key.substr(this.prefix.length));
            }
        }
    }
    setItem(name, value) {
        this.cookieService.put(this.prefix + name, value);
    }
    getItem(name) {
        return this.cookieService.get(this.prefix + name);
    }
    removeItem(name) {
        this.cookieService.remove(this.prefix + name);
    }
}
class MemoryStorage {
    constructor() {
        this.map = {};
    }
    clear() {
        this.map = {};
    }
    setItem(name, value) {
        this.map[name] = value;
    }
    getItem(name) {
        return this.map[name];
    }
    removeItem(name) {
        delete this.map[name];
    }
}
class LocalStorageService {
    constructor(windowService, cookieService) {
        this.windowService = windowService;
        this.cookieService = cookieService;
        this.storage = this.findStorage();
    }
    getItem(name) {
        return this.storage.getItem(name);
    }
    setItem(name, value) {
        this.storage.setItem(name, value);
    }
    removeItem(name) {
        this.storage.removeItem(name);
    }
    clear() {
        this.storage.clear();
    }
    findStorage() {
        // check in the following order:
        // 1. localStorage
        // 2. sessionStorage
        // 3. cookies
        // 4. memory (useless if you need to persist between page loads)
        const local = localStorage || this.windowService.nativeWindow.localStorage;
        if (this.test(local)) {
            return local;
        }
        const session = sessionStorage || this.windowService.nativeWindow.sessionStorage;
        if (this.test(session)) {
            return session;
        }
        const cookies = this.testCookies();
        if (cookies) {
            return cookies;
        }
        return new MemoryStorage();
    }
    test(storage) {
        try {
            storage.setItem(KEY, 'test');
            storage.removeItem(KEY);
            return true;
        }
        catch (err) {
            return false;
        }
    }
    testCookies() {
        const cookies = new CookieStorage(this.cookieService);
        try {
            cookies.setItem(KEY, 'test');
            cookies.removeItem(KEY);
            return cookies;
        }
        catch (err) {
            return null;
        }
    }
}
LocalStorageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, deps: [{ token: WindowService }, { token: CookieService }], target: i0.ɵɵFactoryTarget.Injectable });
LocalStorageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: WindowService }, { type: CookieService }]; } });

/**
 * Header that may be appended to an HttpRequest to specify the cache time in minutes for the request.
 * If appending this header to a request, you MUST have already provided the RequestCachingInterceptor for the
 * application or you will likely get error stating endpoint found unexpected/unsupported header
 */
const CACHE_REQUEST_MINUTES = 'X-Cache-Request-Minutes';
/**
 * Header that may be appended to an HttpRequest to specify an identifier/tag for the cached request.
 * Appending this to a request provides a way to manually invalidate cached requests based the specified tag.
 * This header is optional. If appending this header to a request:
 * 1) You MUST have already provided the RequestCachingInterceptor for the application or you will likely get errors
 *    stating endpoint found unexpected/unsupported header
 * 2) You MUST append CACHE_REQUEST_MINUTES alongside this header or you may receive unexpected header errors
 */
const CACHE_REQUEST_TAG = 'X-Cache-Request-Tag';
/**
 * Header that may be appended to an HttpRequest that will remove any cached requests with the provided tag
 * prior to making the http call.
 * This will guarantee the request goes to the server to get fresh data.
 * If appending this header to a request, you MUST have already provided the RequestCachingInterceptor for the
 * application or you will likely get error stating endpoint found unexpected/unsupported header
 */
const INVALIDATE_CACHE_TAG = 'X-Invalidate-Cache-Tag';

const appendRequestCacheHeaders = (cacheOptions, headers) => {
    let requestHeaders = headers;
    if (cacheOptions.cacheRequestMinutes) {
        requestHeaders = requestHeaders.append(CACHE_REQUEST_MINUTES, cacheOptions.cacheRequestMinutes);
    }
    if (cacheOptions.cacheRequestTag) {
        requestHeaders = requestHeaders.append(CACHE_REQUEST_TAG, cacheOptions.cacheRequestTag);
    }
    if (cacheOptions.invalidateCacheTag) {
        requestHeaders = requestHeaders.append(INVALIDATE_CACHE_TAG, cacheOptions.invalidateCacheTag);
    }
    return requestHeaders;
};

class RequestCache {
    constructor() {
        this.cache = new Map();
    }
    get(req) {
        const url = req.urlWithParams;
        const cached = this.cache.get(url);
        const isExpired = !!cached ? cached.expireTime.getTime() < Date.now() : true;
        return !!cached && !isExpired ? cached.response : undefined;
    }
    put(req, expireTime, response, requestTag) {
        const url = req.urlWithParams;
        const entry = { url, response, expireTime, invalidationTag: requestTag };
        this.cache.set(url, entry);
        // Clean out expired entries
        this.cache.forEach((expiredEntry) => {
            if (expiredEntry.expireTime.getTime() < Date.now()) {
                this.cache.delete(expiredEntry.url);
            }
        });
    }
    invalidateChangedResourceKeys(req) {
        const invalidateHeaderTag = req.headers.get(INVALIDATE_CACHE_TAG);
        for (const cachedEntry of this.cache.entries()) {
            const url = cachedEntry[0];
            const entryVal = cachedEntry[1];
            if (entryVal.invalidationTag === invalidateHeaderTag) {
                this.cache.delete(url);
            }
        }
    }
}
RequestCache.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCache, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
RequestCache.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCache, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCache, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/* eslint-disable max-len */
/**
 * This interceptor acts to store HttpClient Responses in a singleton cache based on configuration provided via
 * HttpRequest Headers.
 * It is originally based on the interceptor example from angular here
 * https://github.com/angular/angular/blob/main/aio/content/examples/http/src/app/http-interceptors/caching-interceptor.ts
 */
class RequestCacheInterceptor {
    constructor(cache) {
        this.cache = cache;
    }
    intercept(req, next) {
        let request;
        if (req.headers.has(INVALIDATE_CACHE_TAG)) {
            this.cache.invalidateChangedResourceKeys(req);
            request = req.clone({ headers: req.headers.delete(INVALIDATE_CACHE_TAG) });
        }
        else {
            request = req;
        }
        const hasCacheHeader = request.headers.has(CACHE_REQUEST_MINUTES);
        const cacheRequestMinutes = request.headers.get(CACHE_REQUEST_MINUTES);
        const cacheHeaderIsNumeric = cacheRequestMinutes && new RegExp(/^\d+$/).test(cacheRequestMinutes);
        const requestTag = request.headers.get(CACHE_REQUEST_TAG);
        if (hasCacheHeader) {
            if (cacheHeaderIsNumeric) {
                const cachedResponse = this.cache.get(request);
                return !!cachedResponse ? of(cachedResponse) : this.sendRequest(request, next, this.cache, requestTag);
            }
            else {
                console.error('CACHE_REQUEST_MINUTES header must be given a number type representing the maximum minutes a request ' +
                    `payload is to be cached for.\nRequested URL: ${request.urlWithParams}`);
            }
            if (!requestTag) {
                console.warn('CACHE_REQUEST_MINUTES header was found without a CACHE_REQUEST_TAG on the following request: ' +
                    `${request.urlWithParams}\n You will not be able to manually invalidate this cached request without ` +
                    'providing the REQUEST_CACHE_TAG on the request.');
            }
        }
        return next.handle(request);
    }
    sendRequest(req, next, cache, requestTag) {
        const cacheRequestMinutes = req.headers.get(CACHE_REQUEST_MINUTES);
        const maxAgeMinutes = cacheRequestMinutes ? +cacheRequestMinutes : 0;
        const maxAgeMillis = (maxAgeMinutes * 60) / 0.001;
        const expireTime = new Date(Date.now() + maxAgeMillis);
        const request = req.clone({ headers: req.headers.delete(CACHE_REQUEST_MINUTES).delete(CACHE_REQUEST_TAG) });
        return next.handle(request).pipe(tap((event) => {
            if (event instanceof HttpResponse) {
                cache.put(request, expireTime, event, requestTag);
            }
        }));
    }
}
RequestCacheInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCacheInterceptor, deps: [{ token: RequestCache }], target: i0.ɵɵFactoryTarget.Injectable });
RequestCacheInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCacheInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestCacheInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: RequestCache }]; } });

const REQUEST_TRACING_CONFIG = new InjectionToken('psu.utils.request.tracing');

const TRACE_HEADER = 'X-Trace';
class RequestTracingInterceptor {
    constructor(config) {
        this.config = config;
    }
    intercept(req, next) {
        if (req.headers.has(TRACE_HEADER)) {
            return next.handle(req.clone({
                headers: req.headers.append('x-request-id', this.generateRequestId()).delete(TRACE_HEADER),
            }));
        }
        else {
            return next.handle(req);
        }
    }
    /**
     * Generates a request ID, the default is based on the provided application ID and a short uuid.
     *
     * Subclasses can override this method in order to add additional data.
     */
    generateRequestId() {
        return `${this.getApplicationId()}_${this.getShortUuid()}`;
    }
    /**
     * Gets the provided application ID.
     */
    getApplicationId() {
        return this.config.applicationId;
    }
    /**
     * Generates a random short uuid.
     */
    getShortUuid() {
        return generate();
    }
}
RequestTracingInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestTracingInterceptor, deps: [{ token: REQUEST_TRACING_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable });
RequestTracingInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestTracingInterceptor, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RequestTracingInterceptor, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [REQUEST_TRACING_CONFIG]
                }] }]; } });

/**
 * @deprecated Just use a template literal
 */
class UrlBuilder {
    constructor(baseUrl, name) {
        this.baseUrl = baseUrl;
        this.name = name;
    }
    build(path) {
        try {
            // NOTE: you may be tempted to use new URL() here, but alas, it does not correctly deal with base urls.
            // In our case, if you do new Url('myPath', 'http://dev.apps.psu.edu/some-service/resources'),
            // it will result in http://dev.apps.psu.edu/some-service/myPath (stripping out resources)
            const base = this.baseUrl && this.baseUrl.endsWith('/') ? this.baseUrl : this.baseUrl ? `${this.baseUrl}/` : this.baseUrl;
            const newPath = !!path && path.startsWith('/') ? path.substr(1) : path;
            const url = new URL(newPath, base).toString();
            return of(url);
        }
        catch (err) {
            return throwError(`Missing or invalid base URL for ${this.name}: ${this.baseUrl}`);
        }
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { CACHE_REQUEST_MINUTES, CACHE_REQUEST_TAG, CacheInterceptor, CookieService, FocusDirective, FocusModule, HttpErrorService, INVALIDATE_CACHE_TAG, LoadingEvents, LocalStorageService, NO_CACHE_HEADER, REQUEST_TRACING_CONFIG, RequestCache, RequestCacheInterceptor, RequestTracingInterceptor, TRACE_HEADER, UrlBuilder, WindowService, appendRequestCacheHeaders };
